<template>
  <div class="internal-message-detail-container">
    <!-- HEADER -->
    <CustomDialogHeaderComponent :headerTitle="TEXT.title" />

    <!-- CONTENT -->
    <div class="message-content">
      <div class="field-row-with-date">
        <div class="field-group">
          <span class="field-label">{{ TEXT.from }}:</span>
          <span class="field-value">{{ props.componentProps.message.de }}</span>
        </div>
        <span class="date-info">{{ formattedDate }}</span>
      </div>

      <div class="field-group">
        <span class="field-label">{{ TEXT.to }}:</span>
        <span class="field-value">{{ props.componentProps.message.para }}</span>
      </div>

      <div class="subject-section">
        <h4 class="subject-title">{{ props.componentProps.message.asunto }}</h4>
        <p class="message-text">{{ props.componentProps.message.cuerpo || '' }}</p>
      </div>
    </div>

    <!-- BUTTON -->
    <CustomDialogActionButtonsComponent
      :showSecondaryButton="false"
      :primaryButtonText="TEXT.accept"
      :mainAction="handleAccept"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import store from '@/store/store'
import { ModuleNamespaces } from '@/store/types/storeGlobalTypes'
import { useI18n } from 'vue-i18n-composable'
import CustomDialogActionButtonsComponent from '@/components/Dialog/CustomDialog/CustomDialogActionButtonsComponent.vue'
import CustomDialogHeaderComponent from '@/components/Dialog/CustomDialog/CustomDialogHeaderComponent.vue'

interface InternalMessage {
  de: string
  para: string
  asunto: string
  cuerpo: string
  fecha: string | Date
}

const props = defineProps<{
  componentProps: { context: string; message: InternalMessage }
}>()

const { t } = useI18n()

const TEXT = {
  title: t('components.notifications.title'),
  accept: t('action_buttons.accept'),
  from: t('components.notifications.from'),
  to: t('components.notifications.to')
}

const dialogModule: string = ModuleNamespaces.DIALOG

const formattedDate = computed(() => {
  if (!props.componentProps.message.fecha) return ''

  const date =
    typeof props.componentProps.message.fecha === 'string'
      ? new Date(props.componentProps.message.fecha)
      : props.componentProps.message.fecha

  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }

  const dateStr = date.toLocaleDateString('es-ES', options)
  const timeStr = date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })

  // Formato: "Lunes 12/03/2024 19:22h"
  const weekday = dateStr.split(' ')[0].replace(',', '')
  const formattedWeekday = weekday.charAt(0).toUpperCase() + weekday.slice(1)

  return `${formattedWeekday} ${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1)
    .toString()
    .padStart(2, '0')}/${date.getFullYear()} ${timeStr}h`
})

const handleAccept = () => {
  store.dispatch(`${dialogModule}/closeCustomDialog`)
}
</script>

<style lang="scss" scoped>
.internal-message-detail-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: $spacing-md;
}

.message-content {
  display: flex;
  flex-direction: column;
  gap: $spacing-md;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  color: $neutral-grey-800;
  font-family: $inter-font;

  .field-row-with-date {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: $spacing-md;

    .date-info {
      white-space: nowrap;
      flex-shrink: 0;
    }
  }

  .field-group {
    display: flex;
    gap: $spacing-sm;
    align-items: baseline;
  }

  .subject-section {
    margin: 0 $spacing-xl;
    gap: $spacing-xs;

    .subject-title {
      color: $neutral-grey-900;
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: 22px;
      margin-bottom: $spacing-xxs;
    }

    .message-text {
      @include scroll-styles;
      max-height: 200px;
      font-family: $inter-font;
      color: $neutral-grey-900;
      margin: 0;
      line-height: 1.6;
      white-space: pre-wrap;
      word-break: break-word;
      margin-bottom: $spacing-md;
    }
  }
}
</style>
